import { FC, useEffect, useState } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import { Credit } from "@models/credit.model";
import { Budget } from "@models/budget.model";
import { CreateCreditPayload } from "@services/finance/credits/commands/cmdCreateCredit";
import { UpdateCreditPayload } from "@services/finance/credits/commands/cmdUpdateCredit";
import { BUDGET_TYPE_LABELS } from "../../types";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (payload: CreateCreditPayload | UpdateCreditPayload) => void;
  credit: Credit | null;
  budgets: readonly Budget[];
  isSaving: boolean;
}

interface FormState {
  budget_id: string;
  amount: string;
  effective_date: string;
  reason: string;
}

const EMPTY: FormState = { budget_id: "", amount: "", effective_date: "", reason: "" };

const CreditFormModal: FC<Props> = ({ show, onClose, onSave, credit, budgets, isSaving }) => {
  const isEdit = credit !== null;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!show) return;
    if (credit) {
      setForm({
        budget_id: credit.budget_id != null ? String(credit.budget_id) : "",
        amount: String(credit.amount ?? ""),
        effective_date: credit.effective_date ?? "",
        reason: credit.reason ?? "",
      });
    } else {
      setForm({ ...EMPTY, budget_id: budgets.length ? String(budgets[0].id) : "" });
    }
  }, [credit, show, budgets]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleSave = () => {
    const common = {
      amount: parseFloat(form.amount) || 0,
      effective_date: form.effective_date || null,
      reason: form.reason.trim() || null,
      source_type: "MANUAL",
    };
    if (isEdit) {
      onSave({ ...common, budget_id: Number(form.budget_id) } satisfies UpdateCreditPayload);
    } else {
      onSave({ ...common, budget_id: Number(form.budget_id) } satisfies CreateCreditPayload);
    }
  };

  const canSubmit = !!form.budget_id && form.amount !== "" && !isNaN(parseFloat(form.amount));

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isEdit ? "Edit Credit" : "Add Credit"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Budget <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select value={form.budget_id} onChange={(e) => update("budget_id", e.target.value)}>
              <option value="">Select a budget...</option>
              {budgets.map((b) => (
                <option key={b.id} value={b.id}>
                  {BUDGET_TYPE_LABELS[b.budget_type] ?? b.budget_type} · {b.start_date}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Manual credits are unique per budget, date, and source.
            </Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => update("amount", e.target.value)}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => update("effective_date", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-0">
            <Form.Label className="fw-semibold">Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.reason}
              onChange={(e) => update("reason", e.target.value)}
              placeholder="Optional note"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !canSubmit}>
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" /> Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2" />
              {isEdit ? "Save Changes" : "Add Credit"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreditFormModal;
